import {useState} from 'react'
import {Link, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {PageHeading} from '@/components/page-heading'
import {HelpIcon} from '@/components/help-icon'
import {DataTable, type Column} from '@/components/data-table'
import {CeldaEstado} from '@/components/ui/celdas-tabla'
import {PerfilesCargoEstadoFiltro, type FiltroEstado} from '@/components/perfil-cargo-filtro'
import {PerfilCargoPropuestas} from '@/components/perfil-cargo-propuestas'
import {PlanLockedPage} from '@/components/plan-upsell'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {Button} from '@/components/ui/button'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {Tag} from '@/components/ui/tag'
import {Skeleton} from '@/components/ui/skeleton'
import {useOperatorPage} from '@/components/no-access-state'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useTableState} from '@/hooks/common/use-table-state'
import {downloadBlob} from '@/lib/api'
import {
  PERFILES_CARGO_PAGE_SIZE,
  useEmparejamientos,
  usePerfilesCargo,
  type PerfilCargoResumen,
} from '@/hooks/empresa/perfil_cargo/use-perfiles-cargo'
import {translateApiError} from '@/lib/errors'
import {formatDateOnly} from '@/lib/risk'
import {SearchInput} from '@/components/ui/search-input'

const ESTADO_TONE: Record<PerfilCargoResumen['estado'], 'green' | 'amber' | 'neutral'> = {
  confirmado: 'green',
  borrador: 'amber',
  sin_perfil: 'neutral',
}

type TabKey = 'cargos' | 'propuestas'

const TABS: TabKey[] = ['cargos', 'propuestas']

function parseTab(value: string | null): TabKey | null {
  return TABS.find((tab) => tab === value) ?? null
}

/** Aplicar una propuesta es acto de firmante: el botón se apaga con la razón que calculó la API (E10-H01). */
export function PerfilesCargoPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {business, guard} = useOperatorPage(businessId)
  const capabilities = useCapabilities(businessId)
  const godReadOnly = useGodModeReadOnly()
  const table = useTableState('cargo')
  const [estadoFiltro, setEstadoFiltro] = useState<FiltroEstado>('todos')
  const lista = usePerfilesCargo(business ? businessId : undefined, {
    page: table.page,
    search: table.search,
    sortBy: table.sortBy,
    sortDir: table.sortDir,
    extra: {estado: estadoFiltro === 'todos' ? undefined : estadoFiltro},
  })
  const cabecera = useEmparejamientos(business ? businessId : undefined)
  const [searchParams, setSearchParams] = useSearchParams()
  // `downloadBlob` no atrapa nada: suelto en el `onClick`, su 409 no se veía.
  const [bajando, setBajando] = useState(false)
  const [errorDocumentos, setErrorDocumentos] = useState<string | null>(null)

  async function bajarDocumentos() {
    setErrorDocumentos(null)
    setBajando(true)
    try {
      await downloadBlob(`/businesses/${businessId}/sgsst/perfiles-cargo/documentos`, 'perfiles-de-cargo.docx')
    } catch (err) {
      setErrorDocumentos(translateApiError(err))
    } finally {
      setBajando(false)
    }
  }

  if (guard) return guard

  // Los endpoints exigen `require_paid_business`: sin esta puerta el plan gratis quedaba con esqueletos eternos.
  if (capabilities.plan_tier === 'free') {
    return (
      <PlanLockedPage
        feature={t('plans.feature.perfilesCargo')}
        businessId={businessId}
        businessName={business!.name}
      />
    )
  }

  // react-query se rinde en silencio tras dos reintentos: sin esta rama el esqueleto giraría para siempre.
  if (cabecera.isError || lista.isError) {
    return (
      <div className="space-y-3">
        <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="perfiles-cargo-error-carga">
          {t('perfilCargo.errorCarga')}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            void cabecera.refetch()
            void lista.refetch()
          }}>
          {t('perfilCargo.reintentar')}
        </Button>
      </div>
    )
  }

  const puedeAplicar = capabilities.sgsst_firmar && !godReadOnly
  // Quien sí firma solo tiene puesto el cinturón; una membresía virtual nunca firma (`require_firmante`
  // rechaza el modo dios), así que mandarla a soltarlo terminaría en 403.
  const razonBloqueo = godReadOnly
    ? t(capabilities.sgsst_firmar ? 'perfilCargo.bloqueoSeguro' : 'perfilCargo.bloqueoModoDios')
    : capabilities.firmar_bloqueado_por

  const tab = parseTab(searchParams.get('tab')) ?? 'cargos'
  function setTab(next: TabKey) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        params.set('tab', next)
        return params
      },
      {replace: true}
    )
  }

  const columnas: Column<PerfilCargoResumen>[] = [
    {
      key: 'cargo',
      header: t('perfilCargo.columna.cargo'),
      sortable: true,
      cell: (fila) => (
        <Link className="underline" to={`/businesses/${businessId}/sgsst/perfiles-cargo/${fila.position_id}`}>
          {fila.cargo}
        </Link>
      ),
    },
    {
      key: 'personas',
      header: t('perfilCargo.columna.personas'),
      sortable: true,
      width: 'w-32',
      cell: (fila) =>
        fila.personas === 0 ? (
          <CeldaEstado chip={<Tag tone="neutral">{t('perfilCargo.columna.personasCero')}</Tag>} />
        ) : (
          <span className="font-mono text-sm">{fila.personas}</span>
        ),
    },
    {
      key: 'estado',
      header: t('perfilCargo.columna.estado'),
      sortable: true,
      width: 'w-32',
      cell: (fila) => (
        <CeldaEstado chip={<Tag tone={ESTADO_TONE[fila.estado]}>{t(`perfilCargo.estado.${fila.estado}`)}</Tag>} />
      ),
    },
    {
      key: 'origen',
      header: t('perfilCargo.columna.origen'),
      headerAside: (
        <HelpIcon
          label={t('common.help.iconLabelFor', {campo: t('perfilCargo.columna.origen')})}
          testId="perfiles-cargo-origen-help">
          {t('perfilCargo.columna.origenHelp')}
        </HelpIcon>
      ),
      width: 'w-2/5',
      cell: (fila) =>
        fila.origen_catalogo_nombre ? (
          <span className="text-xs text-muted-foreground">
            {/* La cita es lo que el perfil confirmado guarda; si no resuelve fecha se imprime sola, sin inventarla. */}
            {t(fila.origen_catalogo_version_fecha ? 'perfilCargo.rotulo' : 'perfilCargo.rotuloSinFecha', {
              perfil: fila.origen_catalogo_nombre,
              cita: fila.origen_catalogo_version,
              fecha: fila.origen_catalogo_version_fecha
                ? formatDateOnly(fila.origen_catalogo_version_fecha)
                : undefined,
            })}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground" data-testid="perfiles-cargo-origen-vacio">
            —
          </span>
        ),
    },
  ]

  const accion = tab === 'cargos' && (
    <Button asChild data-testid="perfiles-cargo-ir-a-confirmar">
      <Link to={`/businesses/${businessId}/sgsst/perfiles-cargo/confirmar`}>
        {t('requisitoCargo.bloque.desdeLaLista')}
      </Link>
    </Button>
  )

  return (
    <div className="space-y-5" data-testid="perfiles-cargo">
      <PageHeading
        title={t('perfilCargo.title')}
        subtitle={business!.name}
        accion={accion || undefined}
        ayuda={
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('perfilCargo.title')})}
            testId="perfiles-cargo-intro">
            {t('perfilCargo.intro')}
          </HelpIcon>
        }
      />

      <Tabs value={tab} onValueChange={(valor) => setTab(valor as TabKey)}>
        <TabsList>
          <TabsTrigger value="cargos" data-testid="tab-cargos">
            {t('perfilCargo.tabsLista.cargos')}
          </TabsTrigger>
          <TabsTrigger value="propuestas" data-testid="tab-propuestas">
            {cabecera.data && cabecera.data.pendientes > 0
              ? t('perfilCargo.tabsLista.propuestasConteo', {count: cabecera.data.pendientes})
              : t('perfilCargo.tabsLista.propuestas')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="cargos" className="space-y-2 pt-4">
          <div className="flex flex-wrap items-center gap-2">
            <SearchInput
              value={table.searchInput}
              onChange={table.setSearchInput}
              placeholder={t('perfilCargo.buscar')}
              className="max-w-sm"
              data-testid="perfiles-cargo-buscar"
            />
            <PerfilesCargoEstadoFiltro
              estado={estadoFiltro}
              onEstado={(siguiente) => {
                setEstadoFiltro(siguiente)
                table.setPage(1)
              }}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={bajando}
              data-testid="perfiles-descargar-documentos"
              onClick={() => void bajarDocumentos()}>
              {t('carpetaVisita.descargarTodos')}
            </Button>
          </div>
          {errorDocumentos && (
            <p className="notice-amber max-w-2xl rounded-lg px-3 py-2 text-sm" data-testid="perfiles-documento-error">
              {errorDocumentos}
            </p>
          )}
          {!lista.data && <Skeleton className="h-40 w-full" />}
          {lista.data && (
            <>
              <DataTable
                columns={columnas}
                rows={lista.data.items}
                rowKey={(fila) => fila.position_id}
                sortBy={table.sortBy}
                sortDir={table.sortDir}
                onSort={table.toggleSort}
                emptyState={
                  <span data-testid="perfiles-cargo-vacio">
                    {t(table.search || estadoFiltro !== 'todos' ? 'perfilCargo.vacioFiltro' : 'perfilCargo.vacio')}
                  </span>
                }
              />
              <PaginationBar
                page={table.page}
                pageSize={PERFILES_CARGO_PAGE_SIZE}
                total={lista.data.total}
                onPageChange={table.setPage}
              />
            </>
          )}
        </TabsContent>

        <TabsContent value="propuestas" className="pt-4">
          {!cabecera.data && <Skeleton className="h-40 w-full" />}
          {cabecera.data && (
            <PerfilCargoPropuestas
              businessId={businessId!}
              cabecera={cabecera.data}
              puedeAplicar={puedeAplicar}
              razonBloqueo={razonBloqueo}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
